import React from 'react';
import { User } from '../../../types';
import { useAdminTranslation } from '../../../modules/i18n';

interface UserDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({ isOpen, onClose, user }) => {
  const { t } = useAdminTranslation();

  if (!isOpen || !user) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
      <div className='max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white shadow-xl scrollbar-thin dark:bg-dark-background-secondary'>
        <div className='p-6'>
          <div className='mb-4 flex items-center justify-between'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-dark-text'>{t('content.users.detail.title')}</h3>
            <button
              onClick={onClose}
              className='rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
              title={t('content.users.detail.closeButton')}
            >
              <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          </div>

          <div className='space-y-4'>
            <div>
              <h4 className='font-medium text-gray-900 dark:text-dark-text'>{user.username}</h4>
              <p className='text-sm text-gray-500 dark:text-dark-text-secondary'>{user.email}</p>
            </div>

            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.users.detail.fields.role')}</span>
                <span
                  className={`ml-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                    user.role?.toLowerCase() === 'admin'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      : user.role?.toLowerCase() === 'moderator'
                        ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                        : 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'
                  }`}
                >
                  {user.role?.toLowerCase()}
                </span>
              </div>
              <div>
                <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.users.detail.fields.status')}</span>
                <span
                  className={`ml-2 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                    user.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}
                >
                  {user.status === 'ACTIVE' ? t('content.users.status.active') : t('content.users.status.inactive')}
                </span>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.users.detail.fields.created')}</span>
                <p className='text-gray-900 dark:text-dark-text'>{user.createdAt}</p>
              </div>
              <div>
                <span className='font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.users.detail.fields.lastUpdate')}</span>
                <p className='text-gray-900 dark:text-dark-text'>{user.updatedAt || 'Chưa cập nhật'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;
